import React from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { useDashboard } from "./useDashboard.js";

// Dashboard in the admin top-bar theme: white surfaces, gray-800→black gradient icon
// badges, emerald for positive/live states, small uppercase labels.
// Every fact is shown once (no repeated status / time / type / speciality / text blocks).

const BLANK = '____';
const has = (v) => !(v === undefined || v === null || v === '' || v === BLANK);

// Plain text of an HTML string, for "is there real content / is it the same text" checks.
function textOf(html) {
  if (!has(html)) return '';
  try {
    return (new DOMParser().parseFromString(String(html), 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function RichText({ value, className }) {
  return <div className={`rich-text ${className}`}>{parse(DOMPurify.sanitize(value, { FORCE_BODY: true }))}</div>;
}

const ICONS = {
  speakers: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
  certificate: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
  intro: 'M4 6h16M4 12h16M4 18h10',
  agenda: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  speciality: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
};

function Icon({ d, className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// Card with a dark gradient icon badge + title (same badge style as the top-bar brand).
function Panel({ icon, title, aside, children, className = '' }) {
  return (
    <section className={`bg-white rounded-2xl border border-gray-200/70 p-5 ${className}`}>
      <header className="flex items-center gap-2.5 mb-4">
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <Icon d={icon} className="w-5 h-5" />
        </span>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {aside && <span className="ml-auto">{aside}</span>}
      </header>
      {children}
    </section>
  );
}

function Empty() {
  return <p className="text-sm text-gray-400">Not provided</p>;
}

function Avatar({ name, image }) {
  const initials = String(name || '').trim().split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';
  if (image) return <img src={image} alt={name} className="w-10 h-10 rounded-full object-cover shrink-0 ring-2 ring-white" />;
  return (
    <span className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center shrink-0">
      {initials}
    </span>
  );
}

function Person({ name, role, image }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar name={name} image={image} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate" title={name}>{name}</p>
        {role && <p className="text-xs text-gray-500 truncate" title={role}>{role}</p>}
      </div>
    </div>
  );
}

function Chip({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    dark: 'bg-gray-900 text-white',
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export default function Dashboard() {
  const { S, speakers, certificateLabel, tags } = useDashboard();
  const detail = S.data?.detail || {};


  const credit = has(detail.creditPoints) ? `${detail.creditPoints}${has(detail.creditType) ? ` ${detail.creditType}` : ''}` : null;
  const certEnabled = certificateLabel === 'Enabled';
  const certKnown = certEnabled || certificateLabel === 'Disabled';

  // The Description panel = detail.description; Agenda only when it is real and not the same text.
  const introText = textOf(detail.description);
  const agendaText = textOf(detail.agenda);
  const showAgenda = Boolean(agendaText) && agendaText !== introText;

  return (
    <div className="space-y-4 lg:space-y-5">
      {/* The Creative Videos picker is in the Creative modal (MainPage's left column, after
          Settings). Session title, time, type, ID and the moderator are in the AdminLayout header. */}

      {/* Speakers on the left half, spanning both rows; Certificate & Credit above
          Specialities on the right half, together as tall as Speakers. Stacked on phones. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
        <Panel
          icon={ICONS.speakers}
          title="Speakers"
          className="md:row-span-2"
          aside={speakers.length > 1 ? <span className="text-[11px] font-medium text-gray-500">{speakers.length}</span> : null}
        >
          {speakers.length ? (
            // Two speakers per line; a long name or speciality is cut with "…" (title shows it).
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {speakers.map((s) => (
                <Person key={s.id} name={s.name} role={has(s.speciality) ? s.speciality : null} />
              ))}
            </div>
          ) : (
            <Empty />
          )}
        </Panel>

        <Panel icon={ICONS.certificate} title="Certificate & Credit">
          <dl className="space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-sm text-gray-500">Certificate</dt>
              <dd>
                {certKnown ? (
                  <Chip tone={certEnabled ? 'emerald' : 'gray'}>{certEnabled ? 'Enabled' : 'Disabled'}</Chip>
                ) : (
                  <span className="text-sm text-gray-400">Not provided</span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-sm text-gray-500">Credit</dt>
              <dd className="text-sm font-semibold text-gray-900">{credit || <span className="font-normal text-gray-400">Not provided</span>}</dd>
            </div>
          </dl>
        </Panel>

        <Panel icon={ICONS.speciality} title="Specialities">
          {tags.length ? (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <Chip key={t}>{t}</Chip>
              ))}
            </div>
          ) : (
            <Empty />
          )}
        </Panel>
      </div>

      {/* Description: full width, on its own row */}
      <Panel icon={ICONS.intro} title="Description">
        {introText ? <RichText value={detail.description} className="text-sm text-gray-600 leading-relaxed" /> : <Empty />}
      </Panel>

      {showAgenda && (
        <Panel icon={ICONS.agenda} title="Agenda">
          <RichText value={detail.agenda} className="text-sm text-gray-600 leading-relaxed" />
        </Panel>
      )}
    </div>
  );
}
